import React, { useEffect, useState } from 'react';
import { fetchSurveyById, submitResponse } from './api';
import logo from './bitdefender_icon_146218.png';

import './App.css';

export default function PublicSurvey({ surveyId }) {
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!surveyId) return;
    (async () => {
      try {
        const data = await fetchSurveyById(surveyId);
        setSurvey(data);
        document.title = data?.title || 'Survey';
      } catch (error) {
        console.error('Error fetching survey:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [surveyId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen">Loading survey...</div>;
  if (!survey) return <div className="flex items-center justify-center min-h-screen">Survey not found</div>;

  const handleChange = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    // Validate required questions
    for (const q of (survey.questions || [])) {
      if (q.required) {
        const ans = answers[q.id];
        const missing = q.questionType === 'checkbox'
          ? !Array.isArray(ans) || ans.length === 0
          : (ans === undefined || ans === null || String(ans).trim() === '');
        if (missing) {
          alert(`Please answer required question: "${q.text || 'Untitled'}"`);
          setLoading(false);
          return;
        }
      }
    }

    try {
      await submitResponse(surveyId, answers);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('Failed to submit survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-blue-50">
      <header className="flex items-center justify-between px-6 py-4 bg-blue-600 border-b border-blue-700 shadow-md">
        <div className="flex items-center gap-4">
          <a href="https://www.bitdefender.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
            <img src={logo} className="h-10 w-auto object-contain" alt="Bitdefender" />
          </a>
          <h2 className="text-xl font-bold text-white m-0">{survey.title || 'Survey'}</h2>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto">
          {submitted && (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="text-5xl mb-4">✓</div>
              <h2 className="text-3xl font-bold text-blue-900 mb-3">Thank You!</h2>
              <p className="text-blue-600 text-lg">Your response has been recorded successfully.</p>
            </div>
          )}

          {!submitted && (!survey.questions || survey.questions.length === 0) && (
            <div className="text-center text-blue-600 text-sm">No questions</div>
          )}

          {!submitted && survey.questions && (
            <div className="space-y-6">
              {survey.questions.map((q, idx) => (
                <div key={q.id} className="p-6 border border-blue-200 rounded-lg bg-white shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">Question {idx + 1}</div>
                      <div className="text-lg font-semibold text-blue-900">
                        {q.text}
                        {q.required && <span className="text-red-500 ml-2">*</span>}
                      </div>
                    </div>
                  </div>

                  {q.questionType === 'checkbox' && q.options?.map((opt, i) => (
                    <label key={i} className="flex items-center mb-4 cursor-pointer hover:bg-blue-50 p-2 rounded-md transition-colors">
                      <input type="checkbox" onChange={e => {
                        const prev = answers[q.id] || [];
                        if (e.target.checked) handleChange(q.id, [...prev, opt]);
                        else handleChange(q.id, prev.filter(x => x !== opt));
                      }} className="mr-3 w-4 h-4 cursor-pointer" /> 
                      <span className="text-blue-900">{opt}</span>
                    </label>
                  ))}

                  {q.questionType === 'radio' && q.options?.map((opt, i) => (
                    <label key={i} className="flex items-center mb-4 cursor-pointer hover:bg-blue-50 p-2 rounded-md transition-colors">
                      <input type="radio" name={`q-${q.id}`} onChange={() => handleChange(q.id, opt)} className="mr-3 w-4 h-4 cursor-pointer" /> 
                      <span className="text-blue-900">{opt}</span>
                    </label>
                  ))}

                  {q.questionType === 'text' && (
                    <input type="text" className="w-full p-3 border border-blue-300 rounded-md focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" onChange={e => handleChange(q.id, e.target.value)} />
                  )}

                  {q.questionType === 'textarea' && (
                    <textarea className="w-full p-3 border border-blue-300 rounded-md min-h-32 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200" onChange={e => handleChange(q.id, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          )}

          {!submitted && (
            <div className="mt-8">
              <button className="px-6 py-3 rounded-md font-semibold transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg" onClick={handleSubmit}>Submit Survey</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
